import React, { useState, useEffect } from 'react';
import { newsletterBuilderAPI, articlesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Mail, Plus, Send, Eye, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminNewsletterBuilder() {
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState('');
  const [intro, setIntro] = useState('');
  const [builds, setBuilds] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [building, setBuilding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentBuildId, setCurrentBuildId] = useState(null);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    Promise.all([
      articlesAPI.list({ status: 'published', limit: 20 }).then(r => setArticles(r.data.articles || [])).catch(() => {}),
      newsletterBuilderAPI.list().then(r => setBuilds(r.data.builds || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const toggleArticle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBuild = async () => {
    if (!subject.trim()) { toast.error('Subject required'); return; }
    if (!selected.length) { toast.error('Select at least one article'); return; }
    setBuilding(true);
    try {
      const r = await newsletterBuilderAPI.build({ subject, intro_text: intro, article_ids: selected });
      setPreviewHtml(r.data.html);
      setCurrentBuildId(r.data.id);
      setShowPreview(true);
      const r2 = await newsletterBuilderAPI.list();
      setBuilds(r2.data.builds || []);
      toast.success('Newsletter built — preview below');
    } catch { toast.error('Failed to build newsletter'); }
    finally { setBuilding(false); }
  };

  const handleSend = async (id) => {
    setSending(true);
    try {
      const r = await newsletterBuilderAPI.send(id || currentBuildId);
      toast.success(`Sent to ${r.data.recipients} subscribers`);
      const r2 = await newsletterBuilderAPI.list();
      setBuilds(r2.data.builds || []);
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="w-6 h-6 text-[#B89B5E]" /> Newsletter Builder</h1>
        <p className="text-sm text-[#5B616E] mt-1">Compose, preview, and send newsletters to subscribers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm">Compose</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block">Subject line *</label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Your weekly Neural News briefing" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Introduction text</label>
                <Textarea rows={3} value={intro} onChange={e => setIntro(e.target.value)} placeholder="Here's what's happening this week..." />
              </div>
              <div>
                <label className="text-xs font-medium mb-2 block">Select articles ({selected.length} selected)</label>
                {loading ? (
                  <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {articles.map(a => (
                      <div
                        key={a.id}
                        onClick={() => toggleArticle(a.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected.includes(a.id) ? 'border-[#B89B5E] bg-[#B89B5E]/5' : 'border-[#E7E0D6] hover:border-[#B89B5E]/50'}`}
                      >
                        {selected.includes(a.id) && <Check className="w-4 h-4 text-[#B89B5E] flex-shrink-0" />}
                        {a.featured_image && <img src={a.featured_image} alt="" className="w-12 h-10 object-cover rounded flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{a.title}</p>
                          <p className="text-xs text-[#5B616E]">{a.category?.name || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handleBuild} disabled={building} className="w-full bg-[#121316] text-white hover:bg-[#1B1D22]">
                <Eye className="w-4 h-4 mr-2" />
                {building ? 'Building…' : 'Build & Preview'}
              </Button>
            </CardContent>
          </Card>

          {/* HTML Preview */}
          {showPreview && previewHtml && (
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Preview</CardTitle>
                  <Button onClick={() => handleSend(currentBuildId)} disabled={sending} className="bg-green-600 text-white hover:bg-green-700 text-xs">
                    <Send className="w-3 h-3 mr-1" /> {sending ? 'Sending…' : 'Send to subscribers'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <iframe
                  srcDoc={previewHtml}
                  style={{ width: '100%', height: '400px', border: '1px solid #E7E0D6', borderRadius: '8px' }}
                  title="Newsletter preview"
                />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Previous builds */}
        <div>
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm">Previous builds</CardTitle></CardHeader>
            <CardContent>
              {builds.length === 0 ? (
                <p className="text-sm text-[#5B616E] text-center py-4">No builds yet</p>
              ) : (
                <div className="space-y-3">
                  {builds.map(b => (
                    <div key={b.id} className="p-3 border border-[#E7E0D6] rounded-lg">
                      <p className="text-sm font-medium truncate">{b.subject}</p>
                      <p className="text-xs text-[#5B616E]">{b.created_at ? new Date(b.created_at).toLocaleDateString() : ''}</p>
                      <div className="flex gap-1 mt-2">
                        <Badge variant={b.sent ? 'default' : 'outline'} className="text-[10px]">
                          {b.sent ? `Sent to ${b.sent_to}` : 'Draft'}
                        </Badge>
                        {!b.sent && (
                          <Button size="sm" variant="outline" className="text-xs h-5" onClick={() => handleSend(b.id)} disabled={sending}>
                            <Send className="w-2.5 h-2.5 mr-1" /> Send
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
